import { STRAPI_URL, flattenStrapiEntry, strapiAuthHeaders, parseErrorMessage } from './strapi';
import { strapiListPermissionGroupsMaster } from './strapiPermissionMaster';

export type PermissionGroupSummary = {
  numericId: number | null;
  documentId: string | null;
  group_name: string;
};

export type PermissionAccessCatalogItem = {
  numericId: number | null;
  documentId: string | null;
  access_code: string;
  access_name: string;
  group: PermissionGroupSummary | null;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function unwrapPopulateOneEntry(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object') return null;
  if ('data' in (v as object)) {
    const d = (v as { data: unknown }).data;
    if (d && typeof d === 'object' && !Array.isArray(d)) return d as Record<string, unknown>;
    return null;
  }
  if (Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function unwrapDataArray(v: unknown): Record<string, unknown>[] {
  if (!v) return [];
  if (Array.isArray(v)) return v as Record<string, unknown>[];
  const o = asRecord(v);
  if (o && Array.isArray(o.data)) return o.data as Record<string, unknown>[];
  return [];
}

export function unwrapPermissionGroup(rel: unknown): PermissionGroupSummary | null {
  const entry = unwrapPopulateOneEntry(rel);
  if (!entry) return null;
  const s = flattenStrapiEntry(entry);
  const rid = entry.id ?? s.id;
  const d = entry.documentId ?? s.documentId;
  return {
    numericId: rid != null && Number.isFinite(Number(rid)) ? Number(rid) : null,
    documentId: d != null ? String(d) : null,
    group_name: String(s.group_name ?? ''),
  };
}

export function unwrapPermissionAccessCatalogItems(rel: unknown): PermissionAccessCatalogItem[] {
  const raw = unwrapDataArray(rel);
  return raw.map((r) => {
    const s = flattenStrapiEntry(r);
    const rid = r.id ?? s.id;
    const d = r.documentId ?? s.documentId;
    return {
      numericId: rid != null && Number.isFinite(Number(rid)) ? Number(rid) : null,
      documentId: d != null ? String(d) : null,
      access_code: String(s.access_code ?? ''),
      access_name: String(s.access_name ?? ''),
      group: unwrapPermissionGroup(r.permission_group ?? s.permission_group),
    };
  });
}

/**
 * All permission accesses with group, for checkbox UI (grouped in the app).
 * Built from `permission-groups` + shallow `permission_accesses` so we never call
 * `GET /permission-accesses?populate=permission_group` (Strapi rejects that populate path in this project).
 */
export async function strapiListPermissionAccessCatalog(jwt: string): Promise<PermissionAccessCatalogItem[]> {
  const groups = await strapiListPermissionGroupsMaster(jwt);
  const items: PermissionAccessCatalogItem[] = [];
  for (const g of groups) {
    const groupSummary: PermissionGroupSummary = {
      numericId: g.numericId,
      documentId: g.documentId,
      group_name: g.group_name,
    };
    for (const a of g.accesses) {
      items.push({
        numericId: a.numericId,
        documentId: a.documentId,
        access_code: a.access_code,
        access_name: a.access_name,
        group: groupSummary,
      });
    }
  }
  return items.sort((a, b) => a.access_code.localeCompare(b.access_code));
}

export type CreatedPermissionPreset = {
  id: number | null;
  documentId: string | null;
};

function parseCreatePresetBody(body: unknown): CreatedPermissionPreset {
  const b = asRecord(body);
  const entry = b?.data && typeof b.data === 'object' ? (b.data as Record<string, unknown>) : null;
  if (!entry) throw new Error('Invalid permission-preset create response');
  const s = flattenStrapiEntry(entry);
  const rid = entry.id ?? s.id;
  const d = entry.documentId ?? s.documentId;
  return {
    id: rid != null && Number.isFinite(Number(rid)) ? Number(rid) : null,
    documentId: d != null ? String(d) : null,
  };
}

/** Create a per-user preset and link `permission_accesses` (document ids). */
export async function strapiCreatePermissionPresetForUser(
  jwt: string,
  userIntegerId: number,
  permissionAccessDocumentIds: string[],
): Promise<CreatedPermissionPreset> {
  const attempts = [
    { data: { user: userIntegerId, permission_accesses: permissionAccessDocumentIds } },
    { data: { user: userIntegerId, permission_accesses: { set: permissionAccessDocumentIds } } },
  ];
  let lastMsg = '';
  for (const payload of attempts) {
    const res = await fetch(`${STRAPI_URL}/api/permission-presets`, {
      method: 'POST',
      headers: {
        ...strapiAuthHeaders(jwt),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const body = (await res.json().catch(() => ({}))) as unknown;
    if (res.ok) return parseCreatePresetBody(body);
    lastMsg = parseErrorMessage(body, res.status);
  }
  throw new Error(lastMsg || 'Unable to create permission preset');
}

/** Replace preset accesses with the given permission-access document ids. */
export async function strapiUpdatePermissionPresetAccesses(
  jwt: string,
  presetDocumentId: string,
  permissionAccessDocumentIds: string[],
): Promise<void> {
  const tryBodies = [
    { data: { permission_accesses: permissionAccessDocumentIds } },
    { data: { permission_accesses: { set: permissionAccessDocumentIds } } },
  ];
  let lastMsg = '';
  for (const bodyObj of tryBodies) {
    const res = await fetch(
      `${STRAPI_URL}/api/permission-presets/${encodeURIComponent(presetDocumentId)}`,
      {
        method: 'PUT',
        headers: {
          ...strapiAuthHeaders(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyObj),
      },
    );
    const body = (await res.json().catch(() => ({}))) as unknown;
    if (res.ok) return;
    lastMsg = parseErrorMessage(body, res.status);
  }
  throw new Error(lastMsg || 'Unable to update permission preset');
}
